'use client'

import { createContext, useContext } from 'react'
import { UserProfileFragment, WalletUserFragment } from '@/gql/generated/graphql'
import { useQuery, UseQueryExecute } from '@urql/next'
import { GetUserProfileDocument, GetWalletUserDocument } from '@/gql/generated/graphql'
import { isAddress } from 'viem'
import { useUserDisplay } from '@/hooks/use-user-display'
import { useSession } from 'next-auth/react'

type ProfileContextValue = {
  // User profile data
  userProfile?: UserProfileFragment
  walletUser?: WalletUserFragment
  refetchProfile: UseQueryExecute

  // Computed values
  userDisplayName: string
  profilePictureUrl: string
  walletAddresses: string[]
  primaryWallet: string
  profileId?: number

  // Metadata
  walletOrUsername: string
  isWallet: boolean
  isProfileOwner: boolean

  // Loading states
  isLoading: boolean
  error?: Error
}

const ProfileContext = createContext<ProfileContextValue | null>(null)

type ProfileProviderProps = {
  children: React.ReactNode
  walletOrUsername: string
}

export function ProfileProvider({ children, walletOrUsername }: ProfileProviderProps): React.ReactElement {
  const { data: session } = useSession()
  const isWallet = isAddress(walletOrUsername)
  const normalizedWalletOrUsername = isWallet ? walletOrUsername.toLowerCase() : walletOrUsername

  // Query for user profile
  const [profileResult, refetchProfile] = useQuery({
    query: GetUserProfileDocument,
    variables: {
      where: isWallet
        ? {
            _or: [{ linked_accounts: { public_address: { _eq: normalizedWalletOrUsername } } }]
          }
        : { username: { _eq: normalizedWalletOrUsername } }
    }
  })

  // Query for wallet user (fallback if no profile found)
  const [walletUserResult] = useQuery({
    query: GetWalletUserDocument,
    variables: { wallet_address: normalizedWalletOrUsername },
    pause: !isWallet || !!profileResult.data?.user_profiles?.[0] // Only run if we're looking up by wallet and no profile found
  })

  const userProfile = profileResult.data?.user_profiles?.[0]
  const walletUser = walletUserResult.data?.users?.[0]
  const isLoading = profileResult.fetching || (isWallet && !userProfile && walletUserResult.fetching)

  // Computed values
  const walletAddresses = userProfile
    ? [...(userProfile?.linked_accounts?.map((account) => account.public_address) ?? [])]
    : [walletUser?.public_address ?? '']

  const primaryWallet = walletAddresses[0] || ''
  const { displayName: userDisplayName } = useUserDisplay({
    name: userProfile?.name,
    username: userProfile?.username,
    address: primaryWallet || (isAddress(walletOrUsername) ? walletOrUsername : undefined)
  })
  const profilePictureUrl = userProfile?.profile_picture?.url ?? ''
  const profileId = userProfile?.id
  const isProfileOwner = Boolean(session?.user?.profileId && session.user.profileId === profileId)

  const contextValue: ProfileContextValue = {
    userProfile,
    walletUser,
    userDisplayName,
    profilePictureUrl,
    walletAddresses,
    primaryWallet,
    profileId,
    walletOrUsername: normalizedWalletOrUsername,
    isWallet,
    isProfileOwner,
    isLoading,
    error: profileResult.error || walletUserResult.error,
    refetchProfile
  }

  return <ProfileContext.Provider value={contextValue}>{children}</ProfileContext.Provider>
}

export function useProfile(): ProfileContextValue {
  const context = useContext(ProfileContext)
  if (!context) {
    throw new Error('useProfile must be used within a ProfileProvider')
  }
  return context
}

// Convenience hooks for common use cases
export function useProfileId(): number | undefined {
  return useProfile().profileId
}

export function useIsProfileOwner(): boolean {
  return useProfile().isProfileOwner
}

export function useWalletAddresses(): string[] {
  return useProfile().walletAddresses
}

export function useUserDisplayName(): string {
  return useProfile().userDisplayName
}

export function useWalletOrUsername(): string {
  return useProfile().walletOrUsername
}

export function useProfilePictureUrl(): string {
  return useProfile().profilePictureUrl
}

/**
 * Is a profile "claimed", which means the user has logged in or linked this wallet
 *
 * If a user_profile exists that means the user logged in
 */
export function useIsProfileClaimed(): boolean {
  const userProfileId = useProfileId()
  return !!userProfileId
}

export function useIsProfileLoading(): boolean {
  return useProfile().isLoading
}
